//============================================================================
// Name        : se_treeview.ts
// Purpose     : Implement Senitron Treeview control logic
// Created     : 2019-12-31
// Author      : Senitron Team
// Copyright   : Copyright 2019 Senitron Corporation. All rights reserved.
//============================================================================

import axios from "axios";
import * as QueryString from "query-string";

interface IChildNodeInfo {
    id?: number;
    name: string;
    children: IChildNodeInfo[];
}

interface ITreeHandler {
    listName: string;
    listCounter: () => void;
}

export interface ICustomNodeOptions {
    altNodeName: string;
    childrenNodeName: string;
}

export class SeTreeview {
    treeHandles: ITreeHandler[] = [];

    /**
     * Setup all the events that make the tree control work
     */
    setupEvents() {
        $("label input:checkbox").on("change", (e) => {
            const checkbox = $(e.target);
            const nestedList = checkbox.parent().next().next();
            const selectNestedListCheckbox = nestedList.find("label:not([for]) input:checkbox");

            if (checkbox.is(":checked")) {
                return selectNestedListCheckbox.prop("checked", true);
            }
            selectNestedListCheckbox.prop("checked", false);
        });

        $("input:checkbox").on("change", (e) => {
            const checkbox = $(e.target);
            const nestedList = checkbox.siblings("ul");

            if (checkbox.is(":checked")) {
                nestedList.removeClass("slideup");
                nestedList.addClass("slidedown");
            }
            else {
                nestedList.removeClass("slidedown");
                nestedList.addClass("slideup");
            }
        });

        // Connect count buttons
        $(".__setreeview-count-btn").on("click", (e) => {
            e.preventDefault();
            const classes = e.target.classList;
            classes.forEach(curClass => {
                if (curClass.includes("__setreeview")) {
                    return;
                }

                // Extract the control name from the class
                const klass = curClass.split("-");
                klass.pop();
                klass.pop();

                // Somehow invoke the county thingy
                const treeName = klass.join("-");
                const cN = `.${treeName}-spinner`;

                const hTree = this.getTreeHandle(treeName);
                if (hTree.listCounter) {
                    $(cN).css("display", "inline-block");
                    hTree.listCounter();
                }
            });
        });

        // After initialization and all events are set, for better experience
        // make sure that we indicate on the parent nodes of checked nodes that
        // they contained checked children.
        this.markParentsOfChecked();
    }

    countFinished(treeName: string) {
        const cN = `.${treeName}-spinner`;
        $(cN).hide();
    }

    /**
     * Turn off node events so we can reattach them without getting duplicate fires.
     */
    clearEvents() {
        $("label input:checkbox").off("change");
        $("input:checkbox").off("change");
        $(".__setreeview-count-btn").off("click");
    }

    /**
     * On load, check if there are any child nodes checked, and make sure their parents are checked too
     */
    markParentsOfChecked() {
        const activeFilters = $("label input:checkbox:checked").toArray();
        for (const filter of activeFilters) {
            // Traverse it's parents to find the parent checkbox, and mark it checked
            // const parentCheckbox = $(filter).parents("li").parents("li").find("> label > input");
            // parentCheckbox.prop("checked", true);

            // Expand the parent node by default to better show what filter has been selected.
            const parentExpander = $(filter).parents("li").parents("li").find("> input");
            parentExpander.prop("checked", true);

            const nestedList = parentExpander.siblings("ul");
            nestedList.removeClass("slideup");
            nestedList.addClass("slidedown");
        }
    }

    /**
     * Loads the given tree node with the given loadFunc function, which should will return a list
     * of objects with a required name string property, and an optional @id property.
     *
     * @param nodeName
     * @param loadDataUrl
     * @param customNodeOptions Struct of addintional options used to compose the node
     * @param preprocessFunc
     */
    async loadNodes(nodeName: string, loadDataUrl: string, customNodeOptions: ICustomNodeOptions = null, preprocessFunc: (data: IChildNodeInfo[]) => IChildNodeInfo[] = null) {
        const resp = await axios.get(loadDataUrl);
        if (resp.status != 200) {
            window.showErrorDialog("Error loading tree node data");
            return;
        }

        // If we got a preprocess function, call that before populating the node list
        let childrenData = resp.data;
        if (preprocessFunc != null) {
            childrenData = preprocessFunc(childrenData);
        }

        // This should populate the list named @nodeName
        const listNodeName = "." + nodeName.toLowerCase().replace(" ", "_") + "-list";
        const nodeList = $(listNodeName);

        // Loop the data and insert it into the node list
        const nodeHtml = this.insertNodes(nodeName, childrenData, customNodeOptions);
        const allNode = $(`
            <li class="node-unselectable">
                <input type="checkbox" id="node-${nodeName}-all" />
                <label>
                    <input type="checkbox" />
                </label>
                <label for="node-${nodeName}-all" data-record-id="all">All<div></div></label>
                <ul class="slideup ${nodeName}-all-list"></ul>
            </li>
        `);
        nodeList.append(allNode);

        // allNode.append(nodeHtml);
        $(`.${nodeName}-all-list`).append(nodeHtml);

        // Re-attach events
        this.clearEvents();
        this.setupEvents();
    }

    /**
     * Populates a node with the given array of children list entries
     *
     * @param nodeName Name of this node
     * @param childrenData
     * @param customNodeOptions Struct of addintional options used to compose the node
     */
    insertNodes(nodeName: string, childrenData: IChildNodeInfo[], customNodeOptions: ICustomNodeOptions = null) {
        let composedNodeName = nodeName;
        if (customNodeOptions) {
            composedNodeName = customNodeOptions.altNodeName;
        }

        let liList = "";
        for (const it of childrenData) {
            const hasChildren = it.children ? it.children.length > 0 : false;
            const nodeId      = Math.ceil((Math.random() * 900_000) + 1);
            const titleData   = it.name.toLowerCase().replace(" ", "_");
            const safeItId    = this.safeHtml(it.id.toString());

            const html1 = `
            <li>
                <input type="checkbox" id="node-${nodeId}" class="selection-node" />
                <label>
                    <input type="checkbox" name="${composedNodeName}_filter[]" value="${safeItId}" />
                    <span class="check-target"></span>
                </label>
                <label for="node-${nodeId}" data-record-id="${safeItId}">${it.name}<div></div></label>
                <ul class="slideup ${titleData}-list">`;

            const html2 = `
            <li>
                <input type="checkbox" id="node-${nodeId}" class="selection-node" />
                <label>
                    <input type="checkbox" name="${composedNodeName}_filter[]" value="${safeItId}" />
                    <span class="check-target"></span>
                </label>
                <label class="seTreeview-bare-label" for="node-${nodeId}" data-record-id="${safeItId}">${it.name}<div></div></label>
            </li>`;

            liList += hasChildren ? html1 : html2;
            if (hasChildren && customNodeOptions) {
                // Append the children html to this element's html
                customNodeOptions.altNodeName = customNodeOptions.childrenNodeName;
                const childrenList = this.insertNodes(nodeName, it.children, customNodeOptions);
                liList += childrenList;
            }
            if (hasChildren && !customNodeOptions) {
                const childrenList = this.insertNodes(nodeName, it.children, customNodeOptions);
                liList += childrenList;
            }

            // Close the node as appropriate
            liList += hasChildren ? "</ul></li>" : "";
        }
        return liList;
    }

    safeHtml(input: any) {
        input = input.replace(/&/g, "&amp;");
        input = input.replace(/</g, "&lt;");
        input = input.replace(/>/g, "&gt;");
        input = input.replace(/'/g, "");
        input = input.replace(/\//g, "|");
        input = input.replace(/\\/g, "|");

        return input;
    }

    /**
     * Sets the counter function for one of the tree sub-lists
     *
     * @param nodeName
     * @param counterFunc
     */
    setListCounter(nodeName: string, counterFunc: () => void) {
        const hTree: ITreeHandler = this.getTreeHandle(nodeName);
        if (!hTree.listName || hTree.listName == "") {
            // Initialize the tree handle
            hTree.listName    = nodeName;
            hTree.listCounter = counterFunc;

            this.treeHandles.push(hTree);
            return;
        }
        hTree.listCounter = counterFunc;
    }

    /**
     * Based on the filter on the query string auto mark the selected node child elements as checked.
     *
     * @param nodeName Defaut list and node filter name
     * @param customNodeOptions Alternative filter name
     * @param parentNodeName Name of the parent node
     */
    setCheckedNodes(nodeName: string, customNodeOptions: ICustomNodeOptions = null, parentNodeName: string = null) {
        let composedNodeName = nodeName;
        if (customNodeOptions) {
            composedNodeName = customNodeOptions.altNodeName;
        }

        const query = QueryString.parse(location.search);
        const paramName = `${composedNodeName}_filter[]`;
        if (!query[paramName]) {
            if (customNodeOptions) {
                this.setCheckedNodes(customNodeOptions.childrenNodeName, null, nodeName);
            }
            return;
        }

        let filter = query[paramName];
        if (!Array.isArray(filter)) {
            filter = [filter];
        }

        if (parentNodeName) {
            nodeName = parentNodeName;
        }
        for (const nodeId of filter) {
            // Find the category checkbox and check it
            const selector = `.${nodeName}-list :input[name='${composedNodeName}_filter[]'][value='${nodeId}']`;
            const els = $(selector);
            if (els.length > 0) {
                for (const el of Array.from(els)) {
                    $(el).prop("checked", true);

                    // Expand the parent node by default to better show what filter has been selected.
                    const parentExpander = $(el).parents("li").parents("li").find("> input");
                    parentExpander.prop("checked", true);

                    const nestedList = parentExpander.siblings("ul");
                    nestedList.removeClass("slideup");
                    nestedList.addClass("slidedown");
                }
            }
        }
    }

    /**
     * Get handle to a tree control by its name
     *
     * @param treeName   Tree control name
     * @returns          Handle to the tree control
     */
    private getTreeHandle(treeName: string): ITreeHandler {
        let treeHandle = {} as ITreeHandler;
        for (const handle of this.treeHandles) {
            if (handle.listName == treeName) {
                treeHandle = handle;
            }
        }
        return treeHandle;
    }
}
